import type { UseInfiniteQueryResult } from '@tanstack/react-query'
import { useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { Dropdown } from '~/components/Form'
import type {
  DropdownOption,
  SingleDropdownProps
} from '~/components/Form/Input/Dropdown'
import {
  optionItemElementLabel,
  optionItemElementLabelMain,
  optionItemElementSublabel
} from '~/components/Form/Input/Dropdown.css'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { formatAddress } from '~/domains/common/utils'
import { useCompaniesAutocomplete } from '../queries/companyAutocomplete'

export const CompaniesAutocomplete = ({
  ids,
  ...props
}: {
  ids?: string[]
} & Omit<SingleDropdownProps, 'optionsInfiniteQuery' | 'options'>) => {
  const [search, setSearch] = useState<string>()
  const { data, ...rest } = useCompaniesAutocomplete({
    ids: search !== undefined ? undefined : ids,
    search: search
  })
  const options = data?.pages
    .flatMap((page) => page['hydra:member'])
    .map((company) => {
      const formattedLocation = formatAddress(company.address, {
        showLines: false
      })
      return {
        label: company.name,
        value: String(company.id),
        estimateSize: 52,
        element: (
          <div className={optionItemElementLabel}>
            <div className={optionItemElementLabelMain}>{company.name}</div>
            <div className={optionItemElementSublabel}>
              <Badge
                title={company.companyStatus.name}
                backgroundColor={company.companyStatus.color}
              />
              {formattedLocation.length > 0 && (
                <span> · {formattedLocation}</span>
              )}
            </div>
          </div>
        )
      }
    })

  const onSearchChange = useDebouncedCallback((search: string) => {
    setSearch(search)
  }, 300)

  return (
    <Dropdown
      optionsInfiniteQuery={
        {
          ...rest,
          data: options
        } as unknown as UseInfiniteQueryResult<DropdownOption[] | undefined>
      }
      defaultValue={ids?.[0]}
      onSearchChange={(search) => onSearchChange(search ?? '')}
      searchShouldFilterOptions={false}
      {...props}
    />
  )
}
